import { Controller } from "@hotwired/stimulus"
import { Calendar } from '@fullcalendar/core'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'

export default class extends Controller {
  static targets = ["calendar"]
  static values = {
    startDate: String,
    endDate: String,
    project: Object,
    assignments: Array
  }

  connect() {
    console.log("Project Pulse Timeline controller connected")
    console.log("Project data:", this.projectValue)
    console.log("Assignments data:", this.assignmentsValue)
    this.initializeCalendar()
  }

  disconnect() {
    if (this.calendar) {
      this.calendar.destroy()
    }
  }

  initializeCalendar() {
    if (!this.hasCalendarTarget) return

    try {
      // Create resource for the current project
      // Note: We still need this for FullCalendar's internal structure, but we hide the display
      const resource = {
        id: this.projectValue.id.toString(),
        title: this.projectValue.name,
        extendedProps: {
          clientName: this.projectValue.client.name,
          projectName: this.projectValue.name
        }
      }
      
      console.log("Calendar resource:", resource)

      this.calendar = new Calendar(this.calendarTarget, {
        plugins: [resourceTimelinePlugin],
        
        // GPL License key for open source project
        schedulerLicenseKey: 'GPL-My-Project-Is-Open-Source',
        
        // View configuration - timeline with single project as row
        initialView: 'resourceTimelineWeek',
        headerToolbar: false, // We have our own navigation
        
        // Timeline view configuration - exactly 7 day columns
        views: {
          resourceTimelineWeek: {
            type: 'resourceTimeline',
            duration: { weeks: 1 },
            slotDuration: { days: 1 },
            slotLabelInterval: { days: 1 }
          }
        },
        
        // Date configuration
        validRange: {
          start: this.startDateValue,
          end: this.endDateValue
        },
        
        // Week starts on Monday to match our week selector
        firstDay: 1,
        
        // Resource configuration for timeline
        resources: [resource],
        resourceAreaWidth: '0px', // Hide the resource column since we're already in the project context
        resourceLabelText: '',
        
        // Read-only configuration - disable all interactions
        editable: false,
        droppable: false,
        selectable: false,
        selectMirror: false,
        eventInteractive: false,
        eventStartEditable: false,
        eventDurationEditable: false,
        
        // Timeline configuration
        height: 'auto',
        slotMinWidth: 120,
        
        // Remove time-based indicators
        businessHours: false,
        nowIndicator: false,
        
        // Day formatting for timeline headers
        slotLabelFormat: {
          weekday: 'short',
          month: 'numeric',
          day: 'numeric'
        },
        
        // Hide resource area completely
        resourceAreaHeaderContent: '',
        
        // Custom resource rendering (return empty since we're hiding it)
        resourceLabelContent: () => {
          return { html: '' }
        },
        
        // Handle event clicks
        eventClick: (info) => {
          this.handleEventClick(info)
        },

        // Event rendering
        eventDidMount: (info) => {
          // Make events clickable
          info.el.style.cursor = 'pointer'
          info.el.title = `Click to view/edit ${info.event.title} assignment`
          
          // Ensure the event can be properly targeted
          if (info.event.extendedProps.userId) {
            info.el.setAttribute('data-user-id', info.event.extendedProps.userId)
          }
        }
      })

      this.calendar.render()
      
      // Load existing assignments
      this.loadAssignments()
    } catch (error) {
      console.error("Error initializing calendar:", error)
    }
  }

  loadAssignments() {
    if (this.assignmentsValue && Array.isArray(this.assignmentsValue)) {
      this.assignmentsValue.forEach(assignment => {
        // Convert assignment to FullCalendar event format
        const event = {
          id: `assignment-${assignment.id}`,
          title: assignment.user_name,
          resourceId: this.projectValue.id.toString(),
          start: assignment.assignment_date,
          allDay: true,
          backgroundColor: this.getColorForRole(assignment.user_role),
          borderColor: this.getColorForRole(assignment.user_role),
          extendedProps: {
            userId: assignment.user_id,
            userRole: assignment.user_role,
            assignmentType: 'persisted',
            assignmentId: assignment.id,
            description: assignment.description
          }
        }
        
        this.calendar.addEvent(event)
      })
      console.log(`Loaded ${this.assignmentsValue.length} assignments`)
    }
  }

  getColorForRole(role) {
    const colors = {
      'admin': '#ef4444',      // red
      'manager': '#f59e0b',    // amber
      'developer': '#10b981',  // emerald
      'designer': '#8b5cf6',   // violet
      'qa': '#06b6d4',         // cyan
      'employee': '#6b7280'    // gray
    }
    return colors[role] || colors['employee']
  }

  handleEventClick(info) {
    const event = info.event
    const assignmentType = event.extendedProps.assignmentType
    
    if (assignmentType === 'persisted') {
      // Use shared assignment modal controller
      const modalController = this.application.getControllerForElementAndIdentifier(
        document.querySelector('[data-controller*="assignment-modal"]'),
        'assignment-modal'
      )
      
      if (modalController) {
        const projectName = this.projectValue.name || 'Unknown Project'
        modalController.show(event, projectName)
      }
      
      info.jsEvent.preventDefault()
    }
  }

  handleToast(event) {
    // Handle toast events from the shared assignment modal
    const { message, type } = event.detail
    this.showToast(message, type)
  }

  showToast(message, type = 'info') {
    // Simple toast implementation - you might want to use your existing toast system
    console.log(`${type.toUpperCase()}: ${message}`)
    
    // Try to use the existing toast system if available
    if (window.showToast) {
      window.showToast(message, type)
    } else {
      // Fallback to alert for now
      alert(message)
    }
  }
} 